/**
 * @module features/devices/pages/DevicesPage
 * @description Super Admin device fleet page implementing the 4-state contract:
 * loading skeleton, error with retry, empty state, and rich multi-device data view.
 * Connects DeviceInspectorDrawer (View Sheet), EditDeviceModal (Edit Sheet),
 * and DeviceRegisterModal (Register Sheet), all optimized for mobile bottom sheets.
 */
import * as React from "react";
import { AlertCircle, ChevronLeft, ChevronRight, Cpu, PlusCircle, RefreshCw, Upload } from "lucide-react";
import { PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { AppError } from "@/lib/api-client";
import { useDevices } from "../api/useDevices";
import { useDeviceStats, useDeviceTypes } from "../api/useDeviceStats";
import { BulkImportModal } from "../components/BulkImportModal";
import { DeviceCardList } from "../components/DeviceCardList";
import { DeviceFilterBar } from "../components/DeviceFilterBar";
import { DeviceInspectorDrawer } from "../components/DeviceInspectorDrawer";
import { EditDeviceModal } from "../components/EditDeviceModal";
import { DeviceRegisterModal } from "../components/DeviceRegisterModal";
import { DeviceStatsCards } from "../components/DeviceStatsCards";
import { DeviceCardSkeleton, DeviceTableSkeleton } from "../components/DeviceTableSkeleton";
import { DeviceTable } from "../components/DeviceTable";
import { DeviceItem } from "../types";

const PAGE_SIZE = 10;

export function DevicesPage() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [deviceType, setDeviceType] = React.useState("all");
  const [registerOpen, setRegisterOpen] = React.useState(false);
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [editingDevice, setEditingDevice] = React.useState<DeviceItem | null>(null);

  const devicesQuery = useDevices({ page, page_size: PAGE_SIZE, search, status, device_type: deviceType });
  const statsQuery = useDeviceStats();
  const typesQuery = useDeviceTypes();

  const handleSearch = React.useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);
  const handleStatus = React.useCallback((value: string) => {
    setStatus(value);
    setPage(1);
  }, []);
  const handleType = React.useCallback((value: string) => {
    setDeviceType(value);
    setPage(1);
  }, []);

  const { data, isLoading, isError, error, refetch, isFetching } = devicesQuery;
  const filtersActive = Boolean(search) || status !== "all" || deviceType !== "all";
  const err = error as AppError | null;
  const pagination = data?.pagination;

  const refreshAll = React.useCallback(() => {
    refetch();
    statsQuery.refetch();
  }, [refetch, statsQuery]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Device Management"
        description="Provision, inspect and manage the hardware fleet."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAll}
              disabled={isFetching}
              className="h-10 sm:h-9 text-xs"
              aria-label="Refresh devices and metrics"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} className="h-10 sm:h-9 text-xs">
              <Upload className="mr-1.5 h-4 w-4" />
              <span>Bulk import</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setRegisterOpen(true)}
              className="h-10 sm:h-9 text-xs shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
            >
              <PlusCircle className="mr-1.5 h-4 w-4" />
              <span>Register</span>
            </Button>
          </div>
        }
      />

      <DeviceStatsCards
        stats={statsQuery.data}
        types={typesQuery.data ?? []}
        selectedType={deviceType}
        onTypeSelect={handleType}
        isLoading={statsQuery.isLoading}
      />

      <DeviceFilterBar
        search={search}
        status={status}
        deviceType={deviceType}
        types={typesQuery.data ?? []}
        stats={statsQuery.data}
        onSearchChange={handleSearch}
        onStatusChange={handleStatus}
        onTypeChange={handleType}
      />

      {isLoading ? (
        <>
          <DeviceTableSkeleton />
          <DeviceCardSkeleton />
        </>
      ) : isError ? (
        <div role="alert" className="rounded-xl border border-danger/20 bg-danger-soft p-6 sm:p-8 text-center text-danger">
          <AlertCircle className="mx-auto h-8 w-8" aria-hidden="true" />
          <h2 className="mt-3 font-semibold text-base">Failed to load devices</h2>
          <p className="mt-1 text-xs max-w-md mx-auto">
            {err?.errorDetails ?? "An unexpected error occurred while contacting the server."}
          </p>
          {err?.requestId && (
            <p className="mt-2 text-[11px] font-mono text-muted-foreground break-all">Request ID: {err.requestId}</p>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4 bg-card text-foreground">
            Retry
          </Button>
        </div>
      ) : data && data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/50 p-10 sm:p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <Cpu className="h-6 w-6" aria-hidden="true" />
          </div>
          {filtersActive ? (
            <>
              <h2 className="mt-4 font-semibold text-base text-foreground">No devices match your filters</h2>
              <p className="mt-1 text-xs text-muted-foreground">Try a different search term or clear the filters.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 text-xs"
                onClick={() => {
                  setSearch("");
                  setStatus("all");
                  setDeviceType("all");
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <h2 className="mt-4 font-semibold text-base text-foreground">No devices registered yet</h2>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                Register your first device, or import a batch from a CSV file.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2">
                <Button size="sm" onClick={() => setRegisterOpen(true)} className="text-xs">
                  <PlusCircle className="mr-1.5 h-4 w-4" />
                  Register first device
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} className="text-xs">
                  <Upload className="mr-1.5 h-4 w-4" />
                  Bulk import
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <DeviceTable
            devices={data?.items ?? []}
            onSelect={(d) => setSelectedId(d.id)}
            onEdit={(d) => setEditingDevice(d)}
          />
          <DeviceCardList
            devices={data?.items ?? []}
            onSelect={(d) => setSelectedId(d.id)}
            onEdit={(d) => setEditingDevice(d)}
          />

          {pagination && pagination.total_pages > 1 && (
            <nav aria-label="Pagination" className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
              <p className="font-mono">
                {(pagination.page - 1) * pagination.page_size + 1}–
                {Math.min(pagination.page * pagination.page_size, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => setPage(pagination.page - 1)} className="h-10 sm:h-8 gap-1 text-xs">
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Previous</span>
                </Button>
                <span className="font-mono px-1">{pagination.page} / {pagination.total_pages}</span>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.total_pages} onClick={() => setPage(pagination.page + 1)} className="h-10 sm:h-8 gap-1 text-xs">
                  <span>Next</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </nav>
          )}
        </>
      )}

      {/* 1. Device View / Inspector Drawer (Sheet on Mobile) */}
      <DeviceInspectorDrawer
        deviceId={selectedId}
        onClose={() => setSelectedId(null)}
        onEdit={(d) => {
          setSelectedId(null);
          setEditingDevice(d);
        }}
      />

      {/* 2. Device Edit Drawer (Sheet on Mobile) */}
      <EditDeviceModal
        device={editingDevice}
        open={Boolean(editingDevice)}
        onClose={() => setEditingDevice(null)}
      />

      {/* 3. Device Register Drawer (Sheet on Mobile) */}
      <DeviceRegisterModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
      />

      {/* 4. Bulk Import Dialog (Sheet on Mobile) */}
      <BulkImportModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        types={typesQuery.data ?? []}
      />
    </div>
  );
}
